import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { Collapse, Card, CardContent } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import CustomButton from "./CustomButton";

interface FilterContextValue {
  open: boolean;
  toggle: () => void;
}

const FilterContext = createContext<FilterContextValue | null>(null);

function useFilterContext() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error("FilterToggle subcomponents must be used inside FilterToggleProvider");
  return ctx;
}

export function FilterToggleProvider({
  children,
  defaultOpen = false,
}: {
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  return (
    <FilterContext.Provider value={{ open, toggle }}>
      {children}
    </FilterContext.Provider>
  );
}

export function FilterButton({ label }: { label?: string }) {
  const { open, toggle } = useFilterContext();
  const { t } = useTranslation();

  return (
    <CustomButton
      variant="outline"
      icon={<FilterListIcon />}
      iconPosition="left"
      endIcon={
        <KeyboardArrowDownIcon
          sx={{
            transition: "transform 0.3s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      }
      onClick={toggle}
    >
      {label ?? t("common.filters", "Filtros")}
    </CustomButton>
  );
}

export function FilterCollapse({ children }: { children: ReactNode }) {
  const { open } = useFilterContext();

  return (
    <Collapse in={open}>
      <Card variant="outlined">
        <CardContent>{children}</CardContent>
      </Card>
    </Collapse>
  );
}
